import React, { useEffect, useState } from "react";
import { useNavigation } from "../../../hooks";
import {
  getTransmissionHistory,
  TransmissionHistoryItem,
  TransmissionHistoryType,
} from "../../../services/institutionService";
import LoadingSpinner from "../../common/LoadingSpinner";
import Icon from "../../ui/Icon";

const UsageHistoryPage: React.FC = () => {
  const { goBack } = useNavigation();
  const [history, setHistory] = useState<TransmissionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const historyData = await getTransmissionHistory();
        setHistory(historyData);
      } catch (err) {
        console.error("이용내역 조회 실패:", err);
        setError("이용내역을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}. ${month}. ${day}.`;
  };

  const getTransferCycleLabel = (cycle: string | null) => {
    if (!cycle) return "업데이트하지 않음";
    if (cycle === "1/w") return "주 1회";
    if (cycle === "1/m") return "월 1회";
    return cycle;
  };

  const calculateDuration = (
    beginDate: string | null,
    endDate: string | null
  ) => {
    if (!beginDate || !endDate) return "-";
    const begin = new Date(beginDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - begin.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);

    if (years >= 1) {
      return `${years}년`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months}개월`;
    }
  };

  // 이력 유형이 전송요구서 관련인지 확인
  const isTransmissionDemandType = (historyType: string) => {
    return (
      historyType ===
        TransmissionHistoryType.CREATE_DEMAND_AGREEMENT_AND_TOKEN ||
      historyType ===
        TransmissionHistoryType.CREATE_DEMAND_AGREEMENT_AND_TOKEN_RECANTATION
    );
  };

  // 이력 유형이 약관 동의 관련인지 확인
  const isTermsAgreementType = (historyType: string) => {
    return (
      historyType ===
        TransmissionHistoryType.MODIFY_TERMS_AGREEMENT_4_PROVISION ||
      historyType ===
        TransmissionHistoryType.MODIFY_TERMS_REAGREEMENT_4_PROVISION_BY_USER_NO
    );
  };

  // 이력 유형이 항목동의 관련인지 확인
  const isDatasetAgreementType = (historyType: string) => {
    return (
      historyType ===
        TransmissionHistoryType.CREATE_DATASET_AGREEMENT_4_UTILIZATION ||
      historyType ===
        TransmissionHistoryType.MODIFY_DATASET_AGREEMENT_4_UTILIZATION
    );
  };

  // 이력 유형이 데이터 조회 관련인지 확인
  const isDataQueryType = (historyType: string) => {
    return (
      historyType ===
        TransmissionHistoryType.FIND_DEMAND_TOKEN_MEDICAL_DATA_BY_PROVIDE_INSTITUTION ||
      historyType ===
        TransmissionHistoryType.FIND_DEMAND_TOKEN_DIAGNOSTIC_REPORT_BY_USER_NO ||
      historyType ===
        TransmissionHistoryType.FIND_DEMAND_TOKEN_EXPLANATION_OF_BENEFIT_BY_USER_NO ||
      historyType ===
        TransmissionHistoryType.FIND_DEMAND_TOKEN_IMMUNIZATION_BY_USER_NO ||
      historyType ===
        TransmissionHistoryType.FIND_DEMAND_TOKEN_MEDICATION_DISPENSE_BY_USER_NO
    );
  };

  // 이력 카드 렌더링
  const renderHistoryCard = (item: TransmissionHistoryItem, index: number) => {
    const { historyType } = item;

    return (
      <div key={index} className="bg-white rounded-lg shadow-sm p-4 space-y-2">
        {/* 날짜 */}
        <p className="text-sm text-gray-400">{formatDate(item.createdAt)}</p>

        {/* 제목 */}
        <h3 className="text-base font-semibold text-gray-900">
          {item.title}
          {isDataQueryType(historyType) && item.provideInstitutionName && (
            <span> ({item.provideInstitutionName})</span>
          )}
        </h3>

        {/* 전송요구서 등록/철회 */}
        {isTransmissionDemandType(historyType) && (
          <>
            <div className="flex flex-wrap items-center gap-x-4 text-sm text-gray-600">
              <span>
                정기적 전송: {getTransferCycleLabel(item.regularTransferCycle)}
              </span>
              <span className="text-gray-300">|</span>
              <span>
                전송 요구기간:{" "}
                {calculateDuration(
                  item.transmissionDemandAgreementBeginDate,
                  item.transmissionDemandAgreementEndDate
                )}
              </span>
            </div>
            {item.provideInstitutionName && (
              <p className="text-sm text-gray-700">
                {item.provideInstitutionName}{" "}
                {item.dataSetNameList && item.dataSetNameList.length > 0 && (
                  <span className="text-gray-500">
                    ({item.dataSetNameList.join(", ")})
                  </span>
                )}
              </p>
            )}
          </>
        )}

        {/* 약관 동의 변경 */}
        {isTermsAgreementType(historyType) && (
          <div className="space-y-1 text-sm text-gray-600">
            {item.agreedTerms && item.agreedTerms.length > 0 && (
              <p>
                <span className="text-gray-900">동의:</span>{" "}
                {item.agreedTerms.join(", ")}
              </p>
            )}
            {item.disagreedTerms && item.disagreedTerms.length > 0 && (
              <p>
                <span className="text-gray-900">거부:</span>{" "}
                {item.disagreedTerms.join(", ")}
              </p>
            )}
          </div>
        )}

        {/* 항목동의 등록/변경 */}
        {isDatasetAgreementType(historyType) && (
          <div className="space-y-1 text-sm text-gray-600">
            {item.registeredItems && item.registeredItems.length > 0 && (
              <p>
                <span className="text-gray-900">동의:</span>{" "}
                {item.registeredItems.join(", ")}
              </p>
            )}
            {item.withdrawnItems && item.withdrawnItems.length > 0 && (
              <p>
                <span className="text-gray-900">거부:</span>{" "}
                {item.withdrawnItems.join(", ")}
              </p>
            )}
          </div>
        )}

        {/* 데이터 조회 */}
        {isDataQueryType(historyType) && (
          <>
            <div className="flex items-center gap-x-2 text-sm">
              <span className="text-gray-600">
                {item.isRegularTransfer ? "정기적 조회" : "직접 조회"}
              </span>
              <span className="text-gray-300">|</span>
              <span
                className={item.isSuccess ? "text-green-600" : "text-red-600"}
              >
                조회 {item.isSuccess ? "성공" : "실패"}
              </span>
            </div>
            {item.dataSetNameList && item.dataSetNameList.length > 0 && (
              <p className="text-sm text-gray-600">
                {item.dataSetNameList.join(", ")}
              </p>
            )}
          </>
        )}

        {/* 마이데이터 연동 해지 (추가 정보 없음) */}
        {historyType === TransmissionHistoryType.CANCEL_UTILIZATION && null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>        </header>
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          마이데이터 이용내역
        </h1>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {history.length > 0 ? (
            history.map((item, index) => renderHistoryCard(item, index))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">이용내역이 없습니다.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UsageHistoryPage;
